// Someone's accounts, opened from a people table (mock M3 states 16, 17 and 19): the same rows as Your accounts,
// written about them, with nothing to do. Only they can say an account isn't theirs (spec 3.6).
import React from "react";
import { roleLabel, type LensUser, type PersonAccounts } from "@phoneware/lens-domain";
import { firstName, when } from "../format.js";
import { useLoad, useSession, viewerKind } from "../session.js";
import { Callout, Sheet } from "../ui.js";
import { AccountGroups, SummaryChips } from "./AccountRows.js";

export function PersonSheet({ person, dealerName, onClose }: { person: LensUser; dealerName: string | null; onClose: () => void }): React.ReactElement {
  const { api } = useSession();
  const accounts = useLoad<PersonAccounts>(() => api.personAccounts(person.email), [person.email]);
  const kind = viewerKind(person);
  // "Sales at TVC" or "Super admin, Phoneware", and what the facts call the role.
  let roleTitle: string;
  let where: string;
  switch (kind) {
    case "admin":
      roleTitle = "Tier";
      where = `${roleLabel(person.role)}, Phoneware`;
      break;
    case "dealer":
      roleTitle = "Role";
      where = `${roleLabel(person.role)} at ${dealerName ?? "their dealer"}`;
      break;
    default: {
      const unhandled: never = kind;
      throw new Error(`Unknown person ${String(unhandled)}`);
    }
  }
  return (
    <Sheet
      title={person.name}
      wide
      onClose={onClose}
      foot={
        <button className="btn" type="button" data-testid="person-sheet-close" onClick={onClose}>
          Close
        </button>
      }
    >
      <div data-testid="person-sheet">
        {/* #1660 adds linking back: "Only Dana can link an account or say one isn't theirs." */}
        <p className="lede">
          {where}. Only {firstName(person.name)} can say an account isn&rsquo;t theirs.
        </p>
        <dl className="facts person-facts">
          <div>
            <dt>Email</dt>
            <dd>{person.email}</dd>
          </div>
          <div>
            <dt>{roleTitle}</dt>
            <dd>{roleLabel(person.role)}</dd>
          </div>
          <div>
            <dt>Checked</dt>
            <dd>{accounts.data ? when(accounts.data.checkedAt).replace(/^(Today|Yesterday) /, "$1, ") : "Checking"}</dd>
          </div>
        </dl>
        <h3>Accounts</h3>
        {accounts.error ? (
          <Callout kind="stop" icon="triangle-alert">
            {accounts.error.message}
          </Callout>
        ) : accounts.data ? (
          <>
            <SummaryChips accounts={accounts.data} />
            <AccountGroups accounts={accounts.data} person={person} self={false} actions={null} />
          </>
        ) : (
          <div className="empty">Loading accounts.</div>
        )}
      </div>
    </Sheet>
  );
}
